'use client'

import Link from 'next/link'
import { ArrowUpRight, BellRing, CalendarDays, Check, ChevronDown, Clock3, List, Map, MapPin, Search, SlidersHorizontal, Sparkles, Users } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet'
import { BookingSheet } from './booking-sheet'
import { createBookingLaunch } from './booking-launch'
import { CustomerHeader } from './customer-header'
import { discoveryFilterOptions, filterDiscoveryVenues } from './discovery-filters'
import { eventHref, experienceHref, isDiscoverableEvent, isDiscoveryExperience, restaurantHref } from './discovery-index'
import { FavoriteVenueButton } from './favorite-venue-button'
import { GuestStepper, ReservationDatePicker, ReservationTimePicker } from './reservation-controls'
import { LocationCombobox } from './location-combobox'
import type { AvailabilityOption, BookingIntent, DiscoverySearchQuery, PublicOffering, PublicVenue, PublicVenueCatalogState, Venue } from './domain'
import { toBookingVenue } from './venue-presentation'
import { useFavoriteLocations } from './use-favorite-locations'
import { compareVenueNetwork, externalActionDescription, externalActionLabel, externalActionTitle, externalBookingChannel, externalDestinationHost } from './venue-source'
import { DiscoveryVenueImage } from './discovery-venue-image'
import { dateInTimeZone } from '@/lib/zoned-date'

type AvailabilityState = {
  status: 'loading' | 'ready' | 'error'
  options: AvailabilityOption[]
  message?: string
}

type SelectedBooking = {
  venue: Venue
  intent: BookingIntent
  initialOption?: AvailabilityOption
}

export function SearchResults({ catalog, query, locationOptions }: { catalog: PublicVenueCatalogState; query: DiscoverySearchQuery; locationOptions: string[] }) {
  const [availability, setAvailability] = useState<Record<string, AvailabilityState>>({})
  const [availabilityOnly, setAvailabilityOnly] = useState(query.sort === 'availability')
  const [windowMinutes, setWindowMinutes] = useState<60 | 120 | 720>(120)
  const [mobileView, setMobileView] = useState<'list' | 'map'>('list')
  const [selectedBooking, setSelectedBooking] = useState<SelectedBooking | null>(null)
  const [searchDate, setSearchDate] = useState(query.date)
  const [searchTime, setSearchTime] = useState(query.time)
  const [searchPartySize, setSearchPartySize] = useState(query.partySize)
  const [keyword, setKeyword] = useState(query.q)
  const [cuisines, setCuisines] = useState(query.cuisines)
  const [prices, setPrices] = useState(query.prices)
  const [neighborhoods, setNeighborhoods] = useState(query.neighborhoods)
  const [bookingType, setBookingType] = useState(query.bookingType)
  const [sort, setSort] = useState(query.sort)
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false)
  const mobileSearchSheetRef = useRef<HTMLDivElement>(null)
  const favoriteLocations = useFavoriteLocations()

  useEffect(() => {
    if (catalog.status !== 'ready') return
    const controller = new AbortController()
    const entries = catalog.venues.flatMap((venue) => venue.offerings.map((offering) => [offeringKey(venue, offering), venue, offering] as const))
    setAvailability(Object.fromEntries(entries.map(([key]) => [key, { status: 'loading', options: [] }])))

    const load = async () => {
      try {
        const response = await fetch('/api/tables/availability/search-many', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ searches: entries.map(([key, venue, offering]) => ({
            requestId: key,
            tenantSlug: venue.tenantSlug,
            locationSlug: venue.locationSlug,
            experienceId: offering.id,
            partySize: query.partySize,
            dateFrom: query.date,
            dateTo: query.date,
          })) }),
          signal: controller.signal,
        })
        const payload = await response.json()
        if (!response.ok) throw new Error(payload.message ?? 'Availability could not be loaded.')
        const next = Object.fromEntries((payload.results ?? []).map((result: {
          requestId: string
          status: number
          availability?: { dates?: Array<{ options?: AvailabilityOption[] }> }
          error?: { message?: string }
        }) => [result.requestId, result.status === 200
          ? { status: 'ready', options: result.availability?.dates?.[0]?.options ?? [] }
          : { status: 'error', options: [], message: result.error?.message ?? 'Availability could not be loaded.' }]))
        if (!controller.signal.aborted) setAvailability(next)
      } catch (cause) {
        if (controller.signal.aborted) return
        setAvailability(Object.fromEntries(entries.map(([key]) => [key, {
          status: 'error',
          options: [],
          message: cause instanceof Error ? cause.message : 'Availability could not be loaded.',
        }])))
      }
    }

    if (entries.length > 0) void load()
    return () => controller.abort()
  }, [catalog, query.date, query.partySize])

  const baseVenues = useMemo(() => catalog.status === 'ready' ? catalog.venues : [], [catalog])
  const filters = useMemo(() => discoveryFilterOptions(baseVenues), [baseVenues])
  const activeQuery = useMemo(() => ({ ...query, q: keyword, cuisines, prices, neighborhoods, bookingType, sort }), [bookingType, cuisines, keyword, neighborhoods, prices, query, sort])
  const visibleVenues = useMemo(() => {
    const venues = filterDiscoveryVenues(baseVenues, activeQuery)
    const filtered = !availabilityOnly ? venues : venues.filter((venue) => venue.offerings.some((offering) => {
      const state = availability[offeringKey(venue, offering)]
      if (!state || state.status === 'loading') return true
      return nearbyOptions(state.options, query.time, windowMinutes).some((option) => option.bookable && Boolean(option.optionToken))
    }))
    if (sort !== 'availability') return filtered
    return [...filtered].sort((left, right) => compareVenueNetwork(left, right) || earliestAvailability(left, availability) - earliestAvailability(right, availability))
  }, [activeQuery, availability, availabilityOnly, baseVenues, query.time, sort, windowMinutes])

  const activeFilterCount = cuisines.length + prices.length + neighborhoods.length + (bookingType === 'all' ? 0 : 1) + (availabilityOnly ? 1 : 0)

  function toggleFilter<T extends string>(value: T, values: T[], setValues: (next: T[]) => void) {
    setValues(values.includes(value) ? values.filter((item) => item !== value) : [...values, value])
  }

  function openBooking(venue: PublicVenue, offering: PublicOffering, option?: AvailabilityOption, source?: 'notify') {
    const launch = createBookingLaunch({
      locationSlug: venue.locationSlug,
      offering,
      partySize: query.partySize,
      date: query.date,
      option,
      source,
      accessToken: venue.accessToken,
    })
    setSelectedBooking({
      venue: toBookingVenue(venue, offering, catalog.market),
      ...launch,
    })
  }

  return (
    <div className="search-page">
      <CustomerHeader surface="solid" />
      <main>
        <section className="search-toolbar" aria-label="Reservation search controls">
          <div className="mobile-search-summary">
            <button className="mobile-search-summary-values" type="button" onClick={() => setMobileSearchOpen(true)}>
              <span>{formatCompactDate(searchDate)}</span><span>{formatClock(searchTime)}</span><span>{searchPartySize} {searchPartySize === 1 ? 'guest' : 'guests'}</span>
            </button>
            <button className="mobile-search-edit" type="button" aria-label="Adjust your search" onClick={() => setMobileSearchOpen(true)}><SlidersHorizontal /></button>
          </div>
          <form className="desktop-search-form" action="/search" method="get">
            <label className="search-filter-field search-filter-type search-filter-keyword"><Search /><span><small>Restaurant</small><input name="q" value={keyword} onChange={(event) => setKeyword(event.target.value)} placeholder="Name or cuisine" aria-label="Restaurant or cuisine" /></span></label>
            <LocationCombobox className="search-filter-field search-filter-location" variant="toolbar" value={query.location} suggestions={locationOptions} latitude={query.latitude} longitude={query.longitude} />
            <div className="search-filter-date"><ReservationDatePicker className="search-filter-field" name="date" value={searchDate} min={todayDate()} onChange={setSearchDate} /></div>
            <div className="search-filter-time"><ReservationTimePicker className="search-filter-field" name="time" value={searchTime} onChange={setSearchTime} /></div>
            <GuestStepper className="search-filter-field search-filter-party" label="Party" name="partySize" value={searchPartySize} max={12} onChange={setSearchPartySize} />
            {cuisines.map((value) => <input key={value} type="hidden" name="cuisine" value={value} />)}
            {prices.map((value) => <input key={value} type="hidden" name="price" value={value} />)}
            {neighborhoods.map((value) => <input key={value} type="hidden" name="neighborhood" value={value} />)}
            <input type="hidden" name="bookingType" value={bookingType} />
            <input type="hidden" name="sort" value={sort} />
            <button className="search-update" type="submit"><Search /> <span>Update search</span></button>
          </form>
        </section>

        <section className="search-results-heading">
          <div>
            <h1>{visibleVenues.length} {visibleVenues.length === 1 ? 'place' : 'places'} near {query.location}</h1>
            <p>{formatSearchSummary(query)} · Live times where available; otherwise continue to the restaurant’s verified destination.</p>
          </div>
          <div className="search-heading-actions">
            <div className="mobile-results-toggle" aria-label="Results view">
              <button className={mobileView === 'list' ? 'active' : ''} onClick={() => setMobileView('list')}><List /> List</button>
              <button className={mobileView === 'map' ? 'active' : ''} onClick={() => setMobileView('map')}><Map /> Map</button>
            </div>
            <Dialog>
              <DialogTrigger asChild><button className="more-filters"><SlidersHorizontal /> Filters{activeFilterCount > 0 ? <b>{activeFilterCount}</b> : null}</button></DialogTrigger>
              <DialogContent className="search-filter-dialog">
                <DialogHeader><DialogTitle>Filter restaurants</DialogTitle><DialogDescription>Narrow this market by availability and published restaurant details.</DialogDescription></DialogHeader>
                <fieldset><legend>Availability</legend><label><input type="radio" name="availability" checked={!availabilityOnly} onChange={() => setAvailabilityOnly(false)} /> Show all restaurants</label><label><input type="radio" name="availability" checked={availabilityOnly} onChange={() => setAvailabilityOnly(true)} /> Available near my time</label></fieldset>
                <fieldset><legend>Time window</legend><label><input type="radio" name="window" checked={windowMinutes === 60} onChange={() => setWindowMinutes(60)} /> Within 1 hour</label><label><input type="radio" name="window" checked={windowMinutes === 120} onChange={() => setWindowMinutes(120)} /> Within 2 hours</label><label><input type="radio" name="window" checked={windowMinutes === 720} onChange={() => setWindowMinutes(720)} /> Any time that day</label></fieldset>
                <FilterChoices legend="Booking type" options={[
                  { label: 'All dining', value: 'all' }, { label: 'Reservations', value: 'reservations' }, { label: 'Experiences', value: 'experiences' }, { label: 'Events', value: 'events' }, { label: 'Private dining', value: 'private' },
                ]} selected={[bookingType]} onToggle={(value) => setBookingType(value as DiscoverySearchQuery['bookingType'])} single />
                {filters.cuisines.length > 0 && <FilterChoices legend="Cuisine" options={filters.cuisines.map((item) => ({ label: item.label, value: item.label, count: item.count }))} selected={cuisines} onToggle={(value) => toggleFilter(value, cuisines, setCuisines)} />}
                {filters.prices.length > 0 && <FilterChoices legend="Price" options={filters.prices.map((item) => ({ label: item.label, value: item.label, count: item.count }))} selected={prices} onToggle={(value) => toggleFilter(value as '$' | '$$' | '$$$' | '$$$$', prices, setPrices)} />}
                {filters.neighborhoods.length > 0 && <FilterChoices legend="Neighborhood" options={filters.neighborhoods.map((item) => ({ label: item.label, value: item.label, count: item.count }))} selected={neighborhoods} onToggle={(value) => toggleFilter(value, neighborhoods, setNeighborhoods)} />}
                <DialogFooter showCloseButton />
              </DialogContent>
            </Dialog>
            <label className="search-sort">Sort <select value={sort} onChange={(event) => setSort(event.target.value as DiscoverySearchQuery['sort'])}><option value="recommended">Recommended</option><option value="availability">Earliest availability</option><option value="distance">Distance</option><option value="name">Name</option></select><ChevronDown /></label>
          </div>
        </section>

        {activeFilterCount > 0 && <div className="active-search-filters" aria-label="Active filters">
          {availabilityOnly && <button onClick={() => setAvailabilityOnly(false)}>Available near {formatClock(query.time)} <span>×</span></button>}
          {bookingType !== 'all' && <button onClick={() => setBookingType('all')}>{bookingTypeLabel(bookingType)} <span>×</span></button>}
          {cuisines.map((value) => <button key={value} onClick={() => toggleFilter(value, cuisines, setCuisines)}>{value} <span>×</span></button>)}
          {prices.map((value) => <button key={value} onClick={() => toggleFilter(value, prices, setPrices)}>{value} <span>×</span></button>)}
          {neighborhoods.map((value) => <button key={value} onClick={() => toggleFilter(value, neighborhoods, setNeighborhoods)}>{value} <span>×</span></button>)}
          <button className="clear-search-filters" onClick={() => { setAvailabilityOnly(false); setBookingType('all'); setCuisines([]); setPrices([]); setNeighborhoods([]) }}>Clear all</button>
        </div>}

        <div className={`search-results-layout search-results-layout--${mobileView}`}>
          <section className="search-results-list" aria-live="polite">
            {catalog.status === 'unavailable' && <SearchUnavailable message={catalog.message} />}
            {catalog.status === 'ready' && visibleVenues.length === 0 && <SearchEmpty market={catalog.market} filtered={activeFilterCount > 0 || Boolean(keyword)} onClear={() => { setAvailabilityOnly(false); setBookingType('all'); setCuisines([]); setPrices([]); setNeighborhoods([]); setKeyword('') }} />}
            {visibleVenues.map((venue) => (
              <article className="search-venue-result" key={venue.publicWidgetKey}>
                <header>
                  <div><p>{venue.neighborhood || venue.market || catalog.market}</p><h2><Link href={restaurantHref(venue)}>{venue.publicName}</Link></h2><small>{[venue.priceRange, venue.cuisine, venue.distanceKm === undefined ? null : `${venue.distanceKm.toFixed(1)} km`].filter(Boolean).join(' · ')}</small>{externalBookingChannel(venue) ? null : <FavoriteVenueButton venueName={venue.publicName} active={favoriteLocations.isFavorite(venue.locationSlug)} busy={favoriteLocations.pending === venue.locationSlug} onToggle={() => void favoriteLocations.toggle(venue.locationSlug)} />}</div>
                  <Link className="search-venue-image" href={restaurantHref(venue)} aria-label={`View ${venue.publicName}`}><DiscoveryVenueImage venue={venue} alt={externalBookingChannel(venue)?.imagePresentation === 'representative' ? `${venue.cuisine || 'Restaurant'} dining atmosphere` : `${venue.publicName} restaurant`} sizes="(max-width: 720px) 38vw, 220px" />{externalBookingChannel(venue)?.imagePresentation === 'representative' ? <span>Representative cuisine image</span> : null}</Link>
                </header>
                <SearchResultInventory venue={venue} bookingType={bookingType} query={query} availability={availability} windowMinutes={windowMinutes} onOpenBooking={openBooking} />
              </article>
            ))}
            {catalog.status === 'ready' && catalog.pagination.totalPages > 1 && (
              <nav className="search-pagination" aria-label="Restaurant result pages">
                {catalog.pagination.page > 1
                  ? <Link href={searchPageHref(query, catalog.pagination.page - 1)}>Previous</Link>
                  : <span aria-disabled="true">Previous</span>}
                <small>Page {catalog.pagination.page} of {catalog.pagination.totalPages}</small>
                {catalog.pagination.page < catalog.pagination.totalPages
                  ? <Link href={searchPageHref(query, catalog.pagination.page + 1)}>Next</Link>
                  : <span aria-disabled="true">Next</span>}
              </nav>
            )}
          </section>

          <aside className="search-map" aria-label={`Map of ${query.location}`}>
            <iframe title={`Map of ${query.location}`} src={`https://www.google.com/maps?q=${encodeURIComponent(query.location)}&z=11&output=embed`} loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
            <div className="search-map-caption"><MapPin /><span><strong>{visibleVenues.length} {visibleVenues.length === 1 ? 'restaurant' : 'restaurants'}</strong><small>{query.location}</small></span></div>
          </aside>
        </div>
      </main>

      {selectedBooking && <BookingSheet venue={selectedBooking.venue} initialSearch={selectedBooking.intent} initialOption={selectedBooking.initialOption} onClose={() => setSelectedBooking(null)} />}
      <Sheet open={mobileSearchOpen} onOpenChange={setMobileSearchOpen}>
        <SheetContent ref={mobileSearchSheetRef} side="bottom" className="mobile-search-sheet" showCloseButton>
          <SheetTitle>Adjust your search</SheetTitle>
          <form action="/search" method="get">
            <input type="hidden" name="type" value={query.type} />
            <label className="mobile-search-keyword"><Search /><input name="q" value={keyword} onChange={(event) => setKeyword(event.target.value)} placeholder="Restaurant or cuisine" /></label>
            <LocationCombobox className="mobile-search-location" variant="mobile" value={query.location} suggestions={locationOptions} portalContainer={mobileSearchSheetRef} latitude={query.latitude} longitude={query.longitude} />
            <input type="hidden" name="date" value={searchDate} />
            <div className="mobile-search-calendar"><Calendar mode="single" selected={localDateValue(searchDate)} disabled={{ before: localDateValue(todayDate()) }} onSelect={(nextDate) => { if (nextDate) setSearchDate(toLocalDate(nextDate)) }} /></div>
            <div className="mobile-search-fields">
              <ReservationTimePicker className="mobile-search-field" name="time" value={searchTime} onChange={setSearchTime} />
              <GuestStepper className="mobile-search-field" label="Party size" name="partySize" value={searchPartySize} max={12} onChange={setSearchPartySize} />
            </div>
            {cuisines.map((value) => <input key={value} type="hidden" name="cuisine" value={value} />)}
            {prices.map((value) => <input key={value} type="hidden" name="price" value={value} />)}
            {neighborhoods.map((value) => <input key={value} type="hidden" name="neighborhood" value={value} />)}
            <input type="hidden" name="bookingType" value={bookingType} />
            <input type="hidden" name="sort" value={sort} />
            <Button type="submit" className="button button--burgundy button--full mobile-search-submit">Update search</Button>
          </form>
        </SheetContent>
      </Sheet>
    </div>
  )
}

function SearchUnavailable({ message }: { message: string }) {
  return <div className="search-empty"><Clock3 /><h2>Reservations are unavailable.</h2><p>{message}</p><Button asChild className="button button--burgundy"><Link href="/">Return home</Link></Button></div>
}

function SearchEmpty({ market, filtered, onClear }: { market: string; filtered: boolean; onClear: () => void }) {
  return <div className="search-empty"><MapPin /><h2>No restaurants match this search.</h2><p>{filtered ? 'Try removing a filter, choosing another date, or searching a nearby neighborhood.' : `Try ${market} or change the location above.`}</p>{filtered ? <Button className="button button--burgundy" onClick={onClear}>Clear filters</Button> : <Button asChild className="button button--burgundy"><Link href={`/search?location=${encodeURIComponent(market)}`}>Search {market}</Link></Button>}</div>
}

function SearchResultInventory({ venue, bookingType, query, availability, windowMinutes, onOpenBooking }: {
  venue: PublicVenue
  bookingType: DiscoverySearchQuery['bookingType']
  query: DiscoverySearchQuery
  availability: Record<string, AvailabilityState>
  windowMinutes: number
  onOpenBooking: (venue: PublicVenue, offering: PublicOffering, option?: AvailabilityOption, source?: 'notify') => void
}) {
  const external = externalBookingChannel(venue)
  if (external) {
    const label = externalActionLabel(external)
    const host = externalDestinationHost(external)
    return <div className="search-external-booking">
      <div><small>Reservations</small><strong>{externalActionTitle(external)}</strong><p>{externalActionDescription(external)}</p></div>
      <a href={external.url} target="_blank" rel="noopener noreferrer nofollow" aria-label={`${label} for ${venue.publicName} (opens in a new tab)`}>{label} <ArrowUpRight /></a>
      {host ? <span>You’ll continue to {host}.</span> : null}
    </div>
  }

  if (bookingType === 'events') {
    return <div className="search-event-list">{venue.events.filter((event) => isDiscoverableEvent(event, venue.timezone)).map((event) => {
      const occurrence = nextEventOccurrence(event, venue.timezone)
      return <article key={event.id}><div><small><CalendarDays /> {occurrence ? `${formatEventDate(occurrence.date)} at ${formatClock(occurrence.startTime)}` : 'Restaurant event'}</small><strong><Link href={eventHref(venue, event)}>{event.name}</Link></strong><p>{event.description}</p></div><Link href={eventHref(venue, event)}>View event</Link></article>
    })}</div>
  }

  if (bookingType === 'private') {
    return <div className="search-private-result"><span><Users /><small>Private dining</small></span><div><strong>Plan a group event at {venue.publicName}</strong><p>Share the date, group size, and kind of gathering you have in mind directly with the restaurant.</p></div><Link href={`${restaurantHref(venue)}?private-events=1`}>Start an inquiry</Link></div>
  }

  const offerings = venue.offerings
    .filter((offering) => offering.access === undefined || offering.access === 'public')
    .filter((offering) => bookingType === 'experiences' ? isDiscoveryExperience(offering) : bookingType === 'reservations' ? !isDiscoveryExperience(offering) : true)
    .slice(0, bookingType === 'all' ? 2 : 4)

  return <div className="search-offering-list">{offerings.map((offering) => {
    const key = offeringKey(venue, offering)
    const state = availability[key] ?? { status: 'loading', options: [] }
    const options = nearbyOptions(state.options, query.time, windowMinutes)
    const experience = isDiscoveryExperience(offering)
    return <div className="search-offering" key={key}>
      {experience
        ? <Link className="search-offering-title" href={experienceHref(venue, offering)}><strong>{offering.name}</strong><span><Sparkles /> Experience · {query.partySize} {query.partySize === 1 ? 'guest' : 'guests'}</span></Link>
        : <button className="search-offering-title" onClick={() => onOpenBooking(venue, offering)} aria-label={`View all times for ${offering.name}`}><strong>{offering.name}</strong><span>For {query.partySize} {query.partySize === 1 ? 'guest' : 'guests'}</span></button>}
      {state.status === 'loading' && <div className="search-time-skeleton" aria-label="Loading availability"><i /><i /><i /></div>}
      {state.status === 'error' && <div className="search-offering-status"><span>Availability could not be loaded.</span><button onClick={() => onOpenBooking(venue, offering)}>View offering</button></div>}
      {state.status === 'ready' && options.length > 0 && <div className="search-result-times">{options.map((option) => {
        const bookable = option.bookable && Boolean(option.optionToken)
        const time = formatTime(option.startsAt, venue.timezone)
        return <button className={bookable ? '' : 'is-notify'} key={option.startsAt} aria-label={`${bookable ? 'Book' : 'Get notified for'} ${offering.name} at ${time}`} onClick={() => onOpenBooking(venue, offering, option, bookable ? undefined : 'notify')}><span>{time}</span><strong>{bookable ? 'Book' : 'Notify'}</strong></button>
      })}</div>}
      {state.status === 'ready' && options.length === 0 && <div className="search-offering-status"><span>No matching tables near {formatClock(query.time)}.</span><button onClick={() => onOpenBooking(venue, offering, undefined, 'notify')}><BellRing /> Get notified</button></div>}
    </div>
  })}</div>
}

function FilterChoices({ legend, options, selected, onToggle, single = false }: { legend: string; options: Array<{ label: string; value: string; count?: number }>; selected: string[]; onToggle: (value: string) => void; single?: boolean }) {
  return <fieldset className="search-filter-choices"><legend>{legend}</legend><div>{options.map((option) => { const active = selected.includes(option.value); return <button type="button" aria-pressed={active} key={option.value} onClick={() => onToggle(option.value)}><span className="filter-check">{active ? <Check /> : null}</span><span>{option.label}</span>{option.count !== undefined ? <small>{option.count}</small> : null}{single ? <i /> : null}</button> })}</div></fieldset>
}

function offeringKey(venue: PublicVenue, offering: PublicOffering) {
  return `${venue.publicWidgetKey}:${offering.id}`
}

function nearbyOptions(options: AvailabilityOption[], requestedTime: string, windowMinutes: number) {
  const target = toMinutes(requestedTime)
  return options
    .map((option) => ({ option, distance: Math.abs(toMinutes(option.localStart) - target) }))
    .filter(({ distance }) => distance <= windowMinutes)
    .sort((left, right) => left.distance - right.distance)
    .slice(0, 4)
    .sort((left, right) => left.option.startsAt.localeCompare(right.option.startsAt))
    .map(({ option }) => option)
}

function toMinutes(value: string) {
  const match = value.match(/(\d{1,2}):(\d{2})/)
  return match ? Number(match[1]) * 60 + Number(match[2]) : 0
}

function earliestAvailability(venue: PublicVenue, availability: Record<string, AvailabilityState>) {
  const times = venue.offerings.flatMap((offering) => (availability[offeringKey(venue, offering)]?.options ?? []).filter((option) => option.bookable).map((option) => new Date(option.startsAt).getTime()))
  return times.length > 0 ? Math.min(...times) : Number.POSITIVE_INFINITY
}

function bookingTypeLabel(value: DiscoverySearchQuery['bookingType']) {
  return value === 'private' ? 'Private dining' : `${value.slice(0, 1).toUpperCase()}${value.slice(1)}`
}

function formatSearchSummary(query: DiscoverySearchQuery) {
  const date = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' }).format(new Date(`${query.date}T12:00:00Z`))
  return `${date} at ${formatClock(query.time)} for ${query.partySize} ${query.partySize === 1 ? 'guest' : 'guests'}`
}

function formatClock(value: string) {
  const [hours, minutes] = value.split(':').map(Number)
  return new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'UTC' }).format(new Date(Date.UTC(2026, 0, 1, hours, minutes)))
}

function formatCompactDate(value: string) {
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' }).format(new Date(`${value}T12:00:00Z`))
}

function localDateValue(value: string) {
  return new Date(`${value}T12:00:00`)
}

function toLocalDate(date: Date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function formatTime(value: string, timeZone = 'UTC') {
  return new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', timeZone }).format(new Date(value))
}

function nextEventOccurrence(event: PublicVenue['events'][number], timeZone?: string) {
  const today = dateInTimeZone(new Date(), timeZone ?? 'UTC')
  return [...event.occurrences].filter((occurrence) => occurrence.date >= today && (occurrence.salesStatus ?? 'open') !== 'closed').sort((left, right) => `${left.date}T${left.startTime}`.localeCompare(`${right.date}T${right.startTime}`))[0]
}

function formatEventDate(value: string) {
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' }).format(new Date(`${value}T12:00:00Z`))
}

function todayDate() {
  const date = new Date()
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function searchPageHref(query: DiscoverySearchQuery, page: number) {
  const params = new URLSearchParams({
    type: query.type,
    q: query.q,
    location: query.location,
    date: query.date,
    time: query.time,
    partySize: String(query.partySize),
    page: String(page),
  })
  if (query.latitude !== undefined) params.set('latitude', String(query.latitude))
  if (query.longitude !== undefined) params.set('longitude', String(query.longitude))
  query.cuisines.forEach((value) => params.append('cuisine', value))
  query.prices.forEach((value) => params.append('price', value))
  query.neighborhoods.forEach((value) => params.append('neighborhood', value))
  if (query.bookingType !== 'all') params.set('bookingType', query.bookingType)
  if (query.sort !== 'recommended') params.set('sort', query.sort)
  return `/search?${params}`
}
